/**
 * Reading again when the network dropped a read (15 Sep 2026).
 *
 * A phone that loses its connection for a moment makes fetch reject with a
 * TypeError ("Failed to fetch" in Chrome, "Load failed" in Safari), and the
 * database client put "TypeError: Failed to fetch" on screen. Flujo de caja
 * showed it above $ 0,00.
 *
 * Only reads are repeated. GET and HEAD change nothing; a POST, PATCH or DELETE
 * whose answer was lost may already have been applied, and sending a payment
 * again would record it twice. Only a request that got no answer at all is
 * repeated: an answer from the server, even an error, is returned as it came,
 * and a request the app itself cancelled (an AbortError) is left cancelled.
 *
 * What still fails is thrown under the `offline` name and message, which the
 * database client shows as "name: message".
 */

export const NETWORK_RETRY_DELAYS = [400, 1200];

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

const methodOf = (input, init) => {
  const fromRequest = typeof input === 'object' && input !== null ? input.method : undefined;
  return String(init?.method || fromRequest || 'GET').toUpperCase();
};

// fetch rejects with a TypeError only when no response arrived.
const isNetworkFailure = (error) => error instanceof TypeError;

export const retryingFetch = (baseFetch, { delays = NETWORK_RETRY_DELAYS, offline = null } = {}) =>
  async (input, init) => {
    const isRead = ['GET', 'HEAD'].includes(methodOf(input, init));
    for (let attempt = 0; ; attempt += 1) {
      try {
        return await baseFetch(input, init);
      } catch (error) {
        if (!isNetworkFailure(error)) throw error;
        if (isRead && attempt < delays.length) {
          await wait(delays[attempt]);
          continue;
        }
        if (!offline) throw error;
        const failure = new Error(offline.message);
        failure.name = offline.name;
        failure.cause = error;
        throw failure;
      }
    }
  };
